/**
 * LegacyEchoScheduler
 * ──────────────────────────────────────────────────────────────────────
 * The pre-backend-wired Echo scheduler experience. Rendered ONLY for
 * `isDemo === true` callers via the SchedulerRoute wrapper — fixtures
 * always flow so demo users get the full original UX (punch list,
 * AI auto-fill, rooming, waitlist, templates, analytics) updated for the
 * scheduler_edits_v2.md spec.
 *
 * NOTE: do not add any `/api/*` calls in this file. The legacy surface is
 * intentionally fixture-only.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  Calendar, Plus, Sparkles, AlertTriangle, Phone,
  CheckCircle2, ScanLine, Loader2, RefreshCw, List, LayoutGrid, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import SchedulerCalendar from '../../components/echo/scheduler/SchedulerCalendar';
import BookingModal from '../../components/echo/scheduler/BookingModal';
import NewAppointmentLightbox from '../../components/echo/scheduler/v2/NewAppointmentLightbox';
import { TemplatesTab, AnalyticsTab } from '../../components/echo/scheduler/SchedulerTabs';
import SchedulerSettings from '../../components/echo/scheduler/SchedulerSettings';
import LensUploadLightbox from '../../components/shared/LensUploadLightbox';
import PunchListRail from '../../components/echo/scheduler/v2/PunchListRail';
import PunchListCategoryDialog from '../../components/echo/scheduler/v2/PunchListCategoryDialog';
import CollisionAlert from '../../components/echo/scheduler/v2/CollisionAlert';
import OverbookingDashboard from '../../components/echo/scheduler/v2/OverbookingDashboard';
import DemoRoleSwitcher from '../../components/echo/scheduler/v2/DemoRoleSwitcher';
import RoomingByRole from '../../components/echo/scheduler/v2/RoomingByRole';
import WaitlistByRole from '../../components/echo/scheduler/v2/WaitlistByRole';
import AddToWaitlistPanel from '../../components/echo/scheduler/v2/AddToWaitlistPanel';
import WeeklyReview from '../../components/echo/scheduler/v2/WeeklyReview';
import AppointmentHoverDetails from '../../components/echo/scheduler/v2/AppointmentHoverDetails';
import CampaignRecallsPanel from '../../components/echo/recalls/CampaignRecallsPanel';
import {
  RiskBreakdownV2, DynamicRecallEngine, RoomingOrchestrator,
  SchedulerCommandCenter,
} from '../../components/echo/scheduler/v2/AISchedulerBundle';
import { DEFAULT_SCHEDULER_ROLE } from '../../lib/schedulerRole';
import {
  DEMO_ROOMS, DEMO_ROOMING_PROVIDERS, DEMO_ROOMING_ALERTS,
  DEMO_WAITLIST_PATIENTS, DEMO_WAITLIST_APPROVALS, DEMO_WAITLIST_ANALYTICS,
  DEMO_VISIT_TYPES, DEMO_PROVIDER_OPTIONS, DEMO_WEEKLY_REVIEW,
  DEMO_FLOW_ITEMS, DEMO_COLLISION, DEMO_OB_ANALYTICS,
  DEMO_GRID_PROVIDERS, DEMO_VISIT_TYPES_CATALOG,
  DEMO_LIGHTBOX_PATIENTS, DEMO_LIGHTBOX_LOCATIONS, DEMO_LIGHTBOX_OPEN_SLOTS,
} from '../../lib/fixtures/schedulerV2Demo';
import ProviderColumnGrid from '../../components/echo/scheduler/v2/ProviderColumnGrid';

/* ═══ PUNCH LIST DEMO DATA ═══ */
const DEMO_PUNCH_LIST = [
  { id: 'pl-1', priority: 'red', patient: 'David Wilson', time: '2:00 PM', issue: '42% no-show risk — no confirmation received', financial: '~$200 at risk', action: 'Call', actionType: 'call', echoHandling: null },
  { id: 'pl-2', priority: 'red', patient: 'Robert Johnson', time: '10:00 AM', issue: 'Insurance inactive — cannot bill', financial: 'Claim denial risk', action: 'Scan', actionType: 'scan', echoHandling: null },
  { id: 'pl-3', priority: 'amber', patient: null, time: '11:30 AM', issue: 'Open slot — 3 waitlist patients match', financial: '$150 potential revenue', action: 'Fill', actionType: 'autofill', echoHandling: null },
  { id: 'pl-4', priority: 'amber', patient: 'Emily Davis', time: '10:30 AM', issue: '$20 copay uncollected', financial: 'Collection risk post-visit', action: 'Collect', actionType: 'collect', echoHandling: null },
  { id: 'pl-5', priority: 'amber', patient: 'Maria Garcia', time: null, issue: 'Annual Wellness due — slot open next week', financial: '$250 recoverable', action: 'Book', actionType: 'book', echoHandling: null },
  { id: 'pl-6', priority: 'blue', patient: 'James Taylor', time: '11:00 AM', issue: '35% no-show risk — 2 reminders sent', financial: '$200 risk', action: null, actionType: null, echoHandling: 'Echo handling' },
  { id: 'pl-7', priority: 'blue', patient: 'Sarah Williams', time: '1:00 PM', issue: 'Intake forms incomplete', financial: 'Intake gap', action: null, actionType: null, echoHandling: 'Reminder sent' },
  { id: 'pl-ob1', priority: 'blue', patient: 'Sarah Chen', time: '2:00 PM', issue: 'Contingency offer sent for 2 PM slot (David Wilson — 67% no-show risk)', financial: 'Sarah responded: Yes · waiting for confirmation', action: null, actionType: null, echoHandling: 'Echo handling' },
  { id: 'pl-ob2', priority: 'blue', patient: 'Robert Johnson', time: '11:00 AM', issue: 'Contingency offer sent for 11 AM slot (James Taylor — 35% no-show risk)', financial: 'Awaiting reply', action: null, actionType: null, echoHandling: 'Offer pending' },
  { id: 'pl-8', priority: 'green', patient: 'John Smith', time: '9:00 AM', issue: 'AI confirmed — insurance verified — checked in', financial: null, action: null, actionType: null, echoHandling: 'Done' },
];

/* ═══ TIMELINE DEMO DATA (now the dominant Today element) ═══ */
const DEMO_CALENDAR_APTS = [
  { id: 'c1', time: '9:00', patient: 'John Smith', type: 'Follow-up', provider: 'Dr. Chen', status: 'confirmed', noShow: 5, copay: null },
  { id: 'c2', time: '9:30', patient: 'Maria Garcia', type: 'New Patient', provider: 'Dr. Wilson', status: 'confirmed', noShow: 12, copay: null },
  { id: 'c3', time: '10:00', patient: 'Robert Johnson', type: 'Sick Visit', provider: 'Dr. Chen', status: 'flagged', noShow: 8, copay: null },
  { id: 'c3b', time: '10:00', patient: 'Amy Nguyen', type: 'Walk-in', provider: 'Dr. Chen', status: 'scheduled', noShow: 14, copay: null },
  { id: 'c4', time: '10:30', patient: 'Emily Davis', type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled', noShow: 22, copay: 20 },
  { id: 'c5', time: '11:00', patient: 'James Taylor', type: 'Wellness', provider: 'Dr. Wilson', status: 'at_risk', noShow: 35, copay: null },
  { id: 'c6', time: '11:30', patient: null, type: null, provider: 'Dr. Chen', status: 'open', noShow: null, copay: null },
  { id: 'c6b', time: '12:30', patient: null, type: null, provider: 'Dr. Roberts', status: 'open', noShow: null, copay: null },
  { id: 'c7', time: '1:00', patient: 'Sarah Williams', type: 'Follow-up', provider: 'Dr. Chen', status: 'scheduled', noShow: 15, copay: null },
  { id: 'c8', time: '2:00', patient: 'David Wilson', type: 'New Patient', provider: 'Dr. Wilson', status: 'at_risk', noShow: 42, copay: null },
  { id: 'c9', time: '3:00', patient: 'Lisa Brown', type: 'Sick Visit', provider: 'Dr. Chen', status: 'scheduled', noShow: 10, copay: null },
];

const CAL_STATUS = {
  confirmed: { bg: '#f0fdf4', border: '#22c55e', aiLine: 'AI confirmed' },
  scheduled: { bg: '#f0f0f0', border: '#d1d5db', aiLine: null },
  flagged: { bg: '#fef2f2', border: '#ef4444', aiLine: 'Insurance flagged' },
  at_risk: { bg: '#fef2f2', border: '#ef4444', aiLine: null },
  open: { bg: 'transparent', border: '#d1d5db', aiLine: null },
};

/* ═══ COMPONENT ═══ */
const LegacyEchoScheduler = () => {
  // Legacy demo surface: isDemo is ALWAYS true by construction (this file is
  // only rendered by the SchedulerRoute wrapper when demo mode is active).
  const isDemo = true;
  const allowBooking = true;
  const [activeTab, setActiveTab] = useState('today');
  const [demoRole, setDemoRole] = useState(DEFAULT_SCHEDULER_ROLE);
  const [punchList, setPunchList] = useState(DEMO_PUNCH_LIST);
  const [calendarApts, setCalendarApts] = useState(DEMO_CALENDAR_APTS);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayError, setTodayError] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPrefill, setBookingPrefill] = useState(null);
  const [lensOpen, setLensOpen] = useState(false);
  const [addWaitlistOpen, setAddWaitlistOpen] = useState(false);
  const [waitlistPatients, setWaitlistPatients] = useState(DEMO_WAITLIST_PATIENTS);
  const [categoryDialog, setCategoryDialog] = useState(null); // 'red' | 'amber' | 'green' | null
  const [collision, setCollision] = useState(DEMO_COLLISION);
  const [obAnalytics, setObAnalytics] = useState(DEMO_OB_ANALYTICS);
  const [activeProviders, setActiveProviders] = useState(DEMO_GRID_PROVIDERS.map((p) => p.id));
  const [focusedApt, setFocusedApt] = useState(null); // appointment lightbox
  const [todayListView, setTodayListView] = useState(false); // grid/list toggle

  const fetchToday = useCallback(async () => {
    setPunchList(DEMO_PUNCH_LIST);
    setTodayLoading(false);
    setTodayError(null);
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const today = new Date();
  const redCount = punchList.filter(p => p.priority === 'red').length;
  const amberCount = punchList.filter(p => p.priority === 'amber').length;
  const greenCount = punchList.filter(p => p.priority === 'green').length;

  // Demo-only AI auto-fill message (never reached in real mode — isDemo guard above).
  const DEMO_AUTOFILL_NAME = 'Amanda Rodriguez';

  const handlePunchAction = (item) => {
    if (item.actionType === 'autofill') {
      toast.success('Echo AI is contacting waitlist patients...', { description: 'Top-ranked matches being called now' });
      if (!isDemo) return;
      setTimeout(() => {
        setPunchList(prev => prev.map(p => p.id === item.id ? { ...p, priority: 'green', issue: `11:30 AM filled — ${DEMO_AUTOFILL_NAME} — AI handled`, echoHandling: 'Done', action: null, actionType: null, financial: null } : p));
        toast.success('11:30 AM slot filled', { description: `${DEMO_AUTOFILL_NAME} confirmed via AI call` });
      }, 3000);
    } else if (item.actionType === 'call') {
      toast.info(`Calling ${item.patient}...`);
    } else if (item.actionType === 'scan') {
      toast.info('Opening Lens scanner...');
    } else if (item.actionType === 'collect') {
      toast.info(`Collecting copay from ${item.patient}`);
    } else if (item.actionType === 'book') {
      toast.info('Opening booking flow...');
    }
  };

  const TABS_ALL = [
    { id: 'today', label: 'Today' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'rooming', label: 'Rooming' },
    { id: 'waitlist', label: 'Waitlist' },
    { id: 'recall', label: 'Recall' },
    { id: 'review', label: 'Review' },
    { id: 'insights', label: 'Insights' },
    { id: 'settings', label: 'Settings' },
  ];
  // Edit 5: MA role hides Waitlist from navigation.
  // Review tab is manager-only.
  const TABS = TABS_ALL.filter(t => {
    if (t.id === 'waitlist' && demoRole === 'ma') return false;
    if (t.id === 'review' && demoRole !== 'manager') return false;
    return true;
  });

  const [weeklyReview, setWeeklyReview] = useState(DEMO_WEEKLY_REVIEW);
  const [flowItems, setFlowItems] = useState(DEMO_FLOW_ITEMS);

  const handleMovePhase = (item, toStatus) => {
    setFlowItems(prev => prev.map(it => {
      if (it.id !== item.id) return it;
      // Clear room assignment when leaving in_exam / with_provider
      const next = { ...it, status: toStatus };
      if (!['in_exam', 'with_provider'].includes(toStatus)) {
        next.roomName = undefined;
      }
      // Clear waitMin once they leave the waiting room
      if (toStatus !== 'waiting_room') next.waitMin = undefined;
      return next;
    }));
    const PHASE_LABEL = {
      scheduled: 'Scheduled',
      arrived: 'Arrived',
      waiting_room: 'Waiting Room',
      in_exam: 'In Exam Room',
      with_provider: 'With Provider',
      ready_checkout: 'Ready for Checkout',
    };
    toast.success(`${item.name} → ${PHASE_LABEL[toStatus]}`);
  };

  const handleAddWaitlist = (data) => {
    const id = `wl-${Date.now()}`;
    const probabilityMap = { '<15': 90, '15-30': 75, '30-60': 60, '60+': 40 };
    setWaitlistPatients(prev => [
      ...prev,
      {
        id,
        probability: probabilityMap[data.arrival] || 60,
        name: data.patient,
        type: DEMO_VISIT_TYPES.find(v => v.id === data.reason)?.label || 'Follow-up',
        addedAt: today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        daysWaiting: 0,
        arrival: data.arrival,
        insuranceActive: true,
        showRate: 80,
        careNeed: null,
        bestMatch: null,
      },
    ]);
    toast.success(`${data.patient} added to waitlist`);
  };

  return (
    <div className="space-y-5" data-testid="echo-scheduler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Schedule</h1>
            <Badge className="text-[9px] border-0 bg-violet-100 text-violet-700 gap-1">
              <Sparkles className="h-2.5 w-2.5" /> Echo-Powered
            </Badge>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DemoRoleSwitcher value={demoRole} onChange={setDemoRole} />
          <button
            onClick={() => setCategoryDialog('red')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-red-200 text-red-600 bg-red-50 rounded-md inline-flex items-center hover:bg-red-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-red-count"
            aria-label={`${redCount} urgent items — click to review`}
          >
            {redCount} urgent
          </button>
          <button
            onClick={() => setCategoryDialog('amber')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-amber-200 text-amber-600 bg-amber-50 rounded-md inline-flex items-center hover:bg-amber-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-amber-count"
            aria-label={`${amberCount} attention items — click to review`}
          >
            {amberCount} attention
          </button>
          <button
            onClick={() => setCategoryDialog('green')}
            className="gap-1.5 px-2.5 py-1.5 text-[11px] font-medium border border-emerald-200 text-emerald-600 bg-emerald-50 rounded-md inline-flex items-center hover:bg-emerald-100 transition-colors"
            style={{ cursor: 'pointer' }}
            data-testid="punch-green-count"
            aria-label={`${greenCount} done items — click to review`}
          >
            {greenCount} done
          </button>
          <Button variant="outline" className="text-xs gap-1.5 h-9" onClick={() => setLensOpen(true)} data-testid="scheduler-lens-btn">
            <ScanLine className="h-3.5 w-3.5" /> Upload with Lens
          </Button>
          <Button className="bg-[#111] hover:bg-[#222] text-xs gap-1.5 h-9" data-testid="new-appointment-btn" disabled={!allowBooking} onClick={() => setBookingOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> New Appointment
          </Button>
        </div>
      </div>

      {/* Sub-navigation tabs */}
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 w-fit" data-testid="scheduler-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "px-4 py-2 rounded-md text-xs font-medium transition-colors",
              activeTab === tab.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            )}
            style={{ border: 'none', cursor: 'pointer' }}
            data-testid={`scheduler-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══ TODAY VIEW (Edit 1: Timeline dominant + Punch List rail) ═══ */}
      {activeTab === 'today' && (
        <div className="flex gap-4" data-testid="today-view">
          {/* Timeline — dominant element, full width minus punch rail */}
          <div className="flex-1 min-w-0 space-y-3" data-testid="today-timeline">
            {/* Collision alert (Front Desk + Manager; MA/Provider don't resolve collisions) */}
            {['front_desk', 'manager'].includes(demoRole) && collision?.active && (
              <CollisionAlert
                collision={collision}
                onConfirmAi={() => {
                  setCollision({ ...collision, active: false });
                  toast.success('Echo handled collision', {
                    description: `Sarah booked for 1:00 PM · ${collision.aiGoodwillAmount} goodwill applied`,
                  });
                }}
                onHandleManually={() => {
                  setCollision({ ...collision, active: false });
                  toast.info('Collision flagged for manual handling');
                }}
              />
            )}
            {/* B · Explainable Risk v2 — inline panel for today's highest-risk appointment */}
            {['front_desk', 'manager'].includes(demoRole) && <RiskBreakdownV2 />}
            <Card className="border-slate-200">
              <div className="flex items-center justify-between px-4 pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-800">Today&apos;s Timeline</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400">{calendarApts.filter(a => a.status !== 'open').length} appointments</span>
                  {/* Grid / List toggle */}
                  <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid="today-listmode-toggle">
                    <button
                      onClick={() => setTodayListView(false)}
                      className={cn("px-1.5 py-1 text-[10px] transition-colors inline-flex items-center", !todayListView ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
                      style={{ border: 'none', cursor: 'pointer' }}
                      data-testid="today-view-grid"
                      title="Grid view"
                    >
                      <LayoutGrid className="h-2.5 w-2.5" />
                    </button>
                    <button
                      onClick={() => setTodayListView(true)}
                      className={cn("px-1.5 py-1 text-[10px] transition-colors inline-flex items-center", todayListView ? "bg-[#111] text-white" : "bg-white text-slate-500 hover:bg-slate-50")}
                      style={{ border: 'none', cursor: 'pointer' }}
                      data-testid="today-view-list"
                      title="List view"
                    >
                      <List className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Provider filter pills */}
              <div className="px-4 pb-2 flex items-center gap-1.5 flex-wrap" data-testid="today-provider-filter">
                <span className="text-[9px] text-slate-400 uppercase font-semibold tracking-wide mr-1">Providers</span>
                {DEMO_GRID_PROVIDERS.map((p) => {
                  const active = activeProviders.includes(p.id);
                  const count = calendarApts.filter(a => a.provider === p.id && a.status !== 'open').length;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setActiveProviders(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={cn(
                        'flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium transition-colors',
                        active ? 'bg-slate-100 text-slate-800' : 'text-slate-400'
                      )}
                      style={{ border: 'none', cursor: 'pointer' }}
                      data-testid={`today-filter-${p.id.replace(/\s+/g, '-')}`}
                    >
                      <div className="h-2 w-2 rounded-full" style={{ background: active ? p.color : '#d1d5db' }} />
                      {p.name}
                      <span className="text-[9px] text-slate-400">({count})</span>
                    </button>
                  );
                })}
              </div>
              <CardContent className="p-0 pb-2">
                {todayLoading && (
                  <div className="flex items-center justify-center py-8" data-testid="today-timeline-loading">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    <span className="ml-2 text-[10px] text-slate-500">Loading…</span>
                  </div>
                )}
                {!todayLoading && todayError && (
                  <div className="mx-4 my-3 p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2" data-testid="today-timeline-error">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    <span className="text-[10px] text-red-700 flex-1">{todayError.message || 'Failed to load'}</span>
                    <button onClick={fetchToday} className="text-[10px] text-red-700 underline gap-1 inline-flex items-center" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="today-timeline-retry">
                      <RefreshCw className="h-3 w-3" /> Retry
                    </button>
                  </div>
                )}
                {!todayLoading && !todayError && calendarApts.length === 0 && (
                  <div className="py-8 text-center" data-testid="today-timeline-empty">
                    <Calendar className="h-6 w-6 text-slate-300 mx-auto mb-1.5" />
                    <p className="text-[11px] text-slate-500">No appointments scheduled for today.</p>
                  </div>
                )}
                {!todayLoading && !todayError && calendarApts.length > 0 && todayListView && (
                  <div className="px-3 pb-3 space-y-2" data-testid="today-list-view">
                    {calendarApts
                      .filter((a) => a.status !== 'open')
                      .sort((a, b) => {
                        const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                        return toMin(a.time) - toMin(b.time);
                      })
                      .map((apt) => {
                        const st = CAL_STATUS[apt.status] || CAL_STATUS.scheduled;
                        const provObj = DEMO_GRID_PROVIDERS.find(p => p.id === apt.provider);
                        return (
                          <AppointmentHoverDetails
                            key={apt.id}
                            apt={apt}
                            providerName={provObj?.name || apt.provider}
                            providerColor={provObj?.color}
                            providers={DEMO_GRID_PROVIDERS.map(p => ({ id: p.id, name: p.name, color: p.color }))}
                            testId={`hover-today-list-${apt.id}`}
                            onQuickEdit={({ patchedApt }) => {
                              setCalendarApts((prev) => prev.map(a => a.id === patchedApt.id ? patchedApt : a));
                              toast.success(`Updated ${patchedApt.patient}`, { description: `${patchedApt.time} · ${patchedApt.provider}${patchedApt.room ? ' · ' + patchedApt.room : ''}` });
                            }}
                          >
                            <button
                              onClick={() => setFocusedApt(apt)}
                              className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 transition-colors"
                              style={{ cursor: 'pointer', borderLeft: `3px solid ${st.border}` }}
                              data-testid={`today-list-row-${apt.id}`}
                            >
                              <span className="text-[11px] font-medium text-slate-500 w-16 shrink-0 inline-flex items-center gap-1">
                                <Clock className="h-2.5 w-2.5" /> {apt.time}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-[12px] font-semibold text-slate-800 truncate">{apt.patient}</p>
                                  {apt.copay && <Badge variant="outline" className="text-[8px] border-amber-200 text-amber-600">${apt.copay}</Badge>}
                                </div>
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  {apt.type} · <span className="inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: provObj?.color }} /> {provObj?.name || apt.provider}
                                </div>
                              </div>
                              {st.aiLine && <span className="text-[9px] text-emerald-600 hidden md:inline">{st.aiLine}</span>}
                              {apt.noShow > 30 && <span className="text-[9px] text-red-600 whitespace-nowrap">⚠ {apt.noShow}%</span>}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 text-[10px] gap-1 shrink-0"
                                onClick={(e) => { e.stopPropagation(); toast.success(`${apt.patient} checked in`); }}
                                data-testid={`today-list-checkin-${apt.id}`}
                              >
                                <CheckCircle2 className="h-2.5 w-2.5" /> Check in
                              </Button>
                            </button>
                          </AppointmentHoverDetails>
                        );
                      })}
                  </div>
                )}
                {!todayLoading && !todayError && calendarApts.length > 0 && !todayListView && (
                  <div className="px-2 pb-2" data-testid="today-multi-provider-grid">
                    <ProviderColumnGrid
                      testId="today-grid"
                      providers={DEMO_GRID_PROVIDERS.filter(p => activeProviders.includes(p.id))}
                      appointments={calendarApts.map(a => ({ ...a, durationMin: 30 }))}
                      hoursStart={9}
                      hoursEnd={17}
                      hourPx={112}
                      minColumnPx={200}
                      onAddSlot={(providerId, time) => { setBookingPrefill({ time, provider: providerId }); setBookingOpen(true); }}
                      renderBlock={(apt) => {
                        const st = CAL_STATUS[apt.status] || CAL_STATUS.scheduled;
                        const provObj = DEMO_GRID_PROVIDERS.find(p => p.id === apt.provider);
                        return (
                          <AppointmentHoverDetails
                            apt={apt}
                            providerName={provObj?.name || apt.provider}
                            providerColor={provObj?.color}
                            providers={DEMO_GRID_PROVIDERS.map(p => ({ id: p.id, name: p.name, color: p.color }))}
                            testId={`hover-today-${apt.id}`}
                            onQuickEdit={({ patchedApt }) => {
                              setCalendarApts((prev) => prev.map(a => a.id === patchedApt.id ? patchedApt : a));
                              toast.success(`Updated ${patchedApt.patient}`, { description: `${patchedApt.time} · ${patchedApt.provider}${patchedApt.room ? ' · ' + patchedApt.room : ''}` });
                            }}
                          >
                            <div
                              className="h-full w-full px-2 py-1 rounded-md cursor-pointer hover:opacity-95 flex flex-col overflow-hidden"
                              style={{ borderLeft: `3px solid ${st.border}`, background: st.bg }}
                              onClick={() => setFocusedApt(apt)}
                              data-testid={`today-apt-${apt.id}`}
                            >
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="text-[11px] font-medium text-slate-800 truncate flex-1">{apt.patient}</span>
                                {apt.copay && (<Badge variant="outline" className="text-[8px] border-amber-200 text-amber-600 px-1 py-0">${apt.copay}</Badge>)}
                              </div>
                              <div className="text-[9px] text-slate-500 truncate leading-tight">{apt.type}</div>
                              {st.aiLine && <div className="text-[8px] text-emerald-600 truncate leading-tight">{apt.status === 'confirmed' ? '✓' : '⚠'} {st.aiLine}</div>}
                              {apt.noShow > 30 && <div className="text-[8px] text-red-600 leading-tight">⚠ {apt.noShow}% risk</div>}
                              <div className="flex-1" />
                              <button
                                onClick={(e) => { e.stopPropagation(); toast.success(`${apt.patient} checked in`); }}
                                className="self-end inline-flex items-center gap-0.5 text-[8px] font-medium text-slate-600 hover:text-slate-900 px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                                style={{ cursor: 'pointer' }}
                                data-testid={`today-checkin-${apt.id}`}
                              >
                                <CheckCircle2 className="h-2 w-2" /> Check in
                              </button>
                            </div>
                          </AppointmentHoverDetails>
                        );
                      }}
                      renderOpenSlot={(apt) => (
                        <div
                          className="h-full w-full px-2 py-1 rounded-md border border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 cursor-pointer flex flex-col overflow-hidden"
                          onClick={() => { setBookingPrefill({ time: apt.time }); setBookingOpen(true); }}
                          data-testid={`today-open-${apt.id}`}
                        >
                          <div className="text-[10px] text-slate-400 italic">Available</div>
                          <Badge className="text-[8px] border-0 bg-slate-100 text-slate-700 gap-0.5 w-fit mt-0.5">
                            <Sparkles className="h-2 w-2" /> 3 matches
                          </Badge>
                        </div>
                      )}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Punch List — compact right rail (260px or 40px collapsed) */}
          <PunchListRail
            items={punchList}
            loading={todayLoading}
            error={todayError}
            onRetry={fetchToday}
            onAction={handlePunchAction}
          />
        </div>
      )}

      {/* ═══ CALENDAR TAB ═══ */}
      {activeTab === 'calendar' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <SchedulerCalendar onBook={(prefill) => { setBookingPrefill(prefill); setBookingOpen(true); }} />
          </div>
        </div>
      )}

      {/* ═══ ROOMING TAB (Edit 3: role-specific views + F Rooming Orchestrator) ═══ */}
      {activeTab === 'rooming' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0 space-y-4">
            <RoomingOrchestrator />
            <RoomingByRole
              role={demoRole}
              items={flowItems}
              rooms={DEMO_ROOMS}
              providers={DEMO_ROOMING_PROVIDERS}
              alerts={DEMO_ROOMING_ALERTS}
              providerId="dr-chen"
              onMoveTo={handleMovePhase}
              onRoomPatient={(p, rooms) => {
                const assignedRoom = rooms[0]?.name || 'Room 3';
                setFlowItems(prev => prev.map(it => it.id === p.id
                  ? { ...it, status: 'in_exam', roomName: assignedRoom, waitMin: undefined }
                  : it
                ));
                toast.success(`${p.name} roomed in ${assignedRoom}`);
              }}
            />
          </div>
        </div>
      )}

      {/* ═══ WAITLIST TAB (Edit 4 + Edit 5) ═══ */}
      {activeTab === 'waitlist' && demoRole !== 'ma' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <WaitlistByRole
              role={demoRole}
              patients={waitlistPatients}
              pendingApprovals={DEMO_WAITLIST_APPROVALS}
              analytics={DEMO_WAITLIST_ANALYTICS}
              onAdd={() => setAddWaitlistOpen(true)}
              onNotify={(p) => toast.info(`Notifying ${p.name}…`)}
              onBook={(p) => toast.success(`Booking ${p.name}…`, { description: 'Overbooking check passed' })}
              onRemove={(p) => {
                setWaitlistPatients(prev => prev.filter(x => x.id !== p.id));
                toast.info(`${p.name} removed from waitlist`);
              }}
              onApprove={(a) => toast.success(`Approved: ${a.patientName}`)}
              onDecline={(a) => toast.info(`Declined: ${a.patientName}`)}
            />
          </div>
        </div>
      )}

      {/* ═══ RECALL TAB (Campaigns · rules · grouping · demographics) ═══ */}
      {activeTab === 'recall' && (
        <div className="space-y-4">
          <CampaignRecallsPanel />
          <div className="pt-2">
            <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wide mb-2">Dynamic recall engine</p>
            <DynamicRecallEngine />
          </div>
        </div>
      )}

      {/* ═══ ANALYTICS / INSIGHTS TAB (Scheduler Command Center · J) ═══ */}
      {activeTab === 'insights' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0 space-y-4">
            <OverbookingDashboard
              data={obAnalytics}
              onApplyRecommendation={(pid, newLimit) => {
                setObAnalytics((prev) => ({
                  ...prev,
                  weeklyCard: { ...prev.weeklyCard, recommendation: null },
                }));
                toast.success(`Applied: ${pid} new limit = ${newLimit}`);
              }}
              onDismissRecommendation={() => {
                setObAnalytics((prev) => ({
                  ...prev,
                  weeklyCard: { ...prev.weeklyCard, recommendation: null },
                }));
                toast.info('Recommendation dismissed');
              }}
            />
            <SchedulerCommandCenter />
            <AnalyticsTab />
          </div>
        </div>
      )}

      {/* ═══ ANALYTICS TAB — kept for backwards compat, identical to insights ═══ */}
      {activeTab === 'analytics' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0 space-y-4">
            <OverbookingDashboard
              data={obAnalytics}
              onApplyRecommendation={(pid, newLimit) => {
                setObAnalytics((prev) => ({
                  ...prev,
                  weeklyCard: { ...prev.weeklyCard, recommendation: null },
                }));
                toast.success(`Applied: ${pid} new limit = ${newLimit}`);
              }}
              onDismissRecommendation={() => {
                setObAnalytics((prev) => ({
                  ...prev,
                  weeklyCard: { ...prev.weeklyCard, recommendation: null },
                }));
                toast.info('Recommendation dismissed');
              }}
            />
            <AnalyticsTab />
          </div>
        </div>
      )}

      {/* ═══ REVIEW TAB (Weekly Scheduler Review — manager-only) ═══ */}
      {activeTab === 'review' && demoRole === 'manager' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <WeeklyReview
              weekStart={weeklyReview.week_start}
              narrative={weeklyReview.narrative}
              providers={weeklyReview.providers}
              onRefresh={() => {
                setWeeklyReview({ ...DEMO_WEEKLY_REVIEW, generated_at: new Date().toISOString() });
                toast.success('Weekly review refreshed');
              }}
              onApplyAll={() => {
                setWeeklyReview(prev => ({
                  ...prev,
                  providers: prev.providers.map(p => ({
                    ...p,
                    current_limit: p.ai_suggested,
                    trend: 'match',
                    rationale: `Applied AI suggestion — current limit now ${p.ai_suggested}.`,
                  })),
                }));
                toast.success('All AI suggestions applied');
              }}
              onApplyOne={(p) => {
                setWeeklyReview(prev => ({
                  ...prev,
                  providers: prev.providers.map(x => x.id === p.id ? {
                    ...x, current_limit: x.ai_suggested, trend: 'match',
                    rationale: `Applied AI suggestion — current limit now ${x.ai_suggested}.`,
                  } : x),
                }));
                toast.success(`Applied for ${p.name}`);
              }}
              onKeepOne={(p) => {
                setWeeklyReview(prev => ({
                  ...prev,
                  providers: prev.providers.map(x => x.id === p.id ? {
                    ...x, ai_suggested: x.current_limit, trend: 'match',
                    rationale: 'Kept current setting.',
                  } : x),
                }));
                toast.info(`Kept ${p.name}'s current setting`);
              }}
            />
          </div>
        </div>
      )}

      {/* ═══ SETTINGS TAB ═══ */}
      {activeTab === 'settings' && (
        <div className="flex gap-4">
          <div className="flex-1 min-w-0"><SchedulerSettings /></div>
        </div>
      )}

      {/* ═══ Unused legacy import reference (keeps TemplatesTab available) ═══ */}
      {false && <TemplatesTab />}

      {/* ═══ MODALS ═══ */}
      <NewAppointmentLightbox
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        prefillTime={bookingPrefill?.time}
        patients={DEMO_LIGHTBOX_PATIENTS}
        locations={DEMO_LIGHTBOX_LOCATIONS}
        providers={DEMO_GRID_PROVIDERS.map(p => p.name)}
        visitTypes={DEMO_VISIT_TYPES_CATALOG}
        openSlots={DEMO_LIGHTBOX_OPEN_SLOTS}
        onConfirm={({ patient, selection }) => {
          const vt = DEMO_VISIT_TYPES_CATALOG.find(v => v.id === selection.visitTypeId);
          toast.success(`Booked ${patient?.name}`, {
            description: `${vt?.name || 'Visit'} · ${selection.slot?.time} · ${selection.provider}`,
          });
          setBookingPrefill(null);
        }}
      />
      {/* Keep BookingModal import alive for Calendar backwards-compat, but hidden */}
      {false && <BookingModal open={false} onOpenChange={() => {}} />}
      <LensUploadLightbox open={lensOpen} onOpenChange={setLensOpen} />
      <AddToWaitlistPanel
        open={addWaitlistOpen}
        onOpenChange={setAddWaitlistOpen}
        onSubmit={handleAddWaitlist}
        visitTypes={DEMO_VISIT_TYPES}
        providers={DEMO_PROVIDER_OPTIONS}
      />
      <PunchListCategoryDialog
        open={!!categoryDialog}
        onOpenChange={(v) => { if (!v) setCategoryDialog(null); }}
        category={categoryDialog}
        items={categoryDialog ? punchList.filter(p => p.priority === categoryDialog) : []}
        onAction={handlePunchAction}
      />
      {/* Focused appointment quick view (Today multi-provider grid) */}
      {focusedApt && (
        <Dialog open={!!focusedApt} onOpenChange={(v) => { if (!v) setFocusedApt(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="today-apt-lightbox">
            <DialogHeader>
              <DialogTitle className="text-[13px] font-semibold">{focusedApt.patient}</DialogTitle>
              <DialogDescription className="text-[11px] text-slate-500">
                {focusedApt.time} · {focusedApt.type} · {focusedApt.provider}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-[11px] text-slate-600">
              <p>No-show risk: <b>{focusedApt.noShow ?? '—'}%</b></p>
              {focusedApt.copay && <p>Copay due: <b className="text-amber-600">${focusedApt.copay}</b></p>}
            </div>
            <DialogFooter className="gap-1.5 pt-2">
              <Button size="sm" variant="outline" className="text-[11px]" onClick={() => { toast.success(`${focusedApt.patient} checked in`); setFocusedApt(null); }}>
                Check in
              </Button>
              <Button size="sm" className="text-[11px] bg-[#111] hover:bg-[#222]" onClick={() => setFocusedApt(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Floating AI Scheduling Assistant is handled at the EchoLayout level (single chatbot).
          When on /echo/scheduler, the layout auto-switches the black chatbot to Scheduling-agent mode
          with tooltip "I'm your Scheduling agent". */}
    </div>
  );
};

export default LegacyEchoScheduler;
